import { Box, Typography } from '@mui/material';

/**
 * The card itself — the hero of the section.
 *
 * A card section should lead with a card, not with a big number and a label.
 * So this is a real object: ISO card proportions, the brand's Midnight Navy
 * field, the candle mark, the holder's name, and the retention date set the
 * way a bank card sets its valid-thru. It is the one bold thing in the
 * section; everything below it is deliberately quiet.
 *
 * The tier is the card's MATERIAL rather than a badge — free is flat navy,
 * Pro catches a brass edge-light, Special Care carries a candlelight sheen.
 * Upgrading should feel like being handed a different card, which a chip
 * reading "PRO" never does.
 */

/** Tier-specific surface treatment. Navy is the field in every case; what
 *  changes is how light falls on it. */
const MATERIAL = {
  free: {
    background: 'linear-gradient(152deg,#16294A 0%,#11223E 55%,#0A1425 100%)',
    border: '1px solid rgba(192,167,134,.22)',
    sheen: null,
  },
  pro: {
    background: 'linear-gradient(152deg,#1B3157 0%,#11223E 58%,#0A1425 100%)',
    border: '1px solid rgba(192,167,134,.55)',
    // Light caught along the top edge, where it would fall on a real card.
    // A wide band across the middle read as glare rather than as a material.
    sheen: 'linear-gradient(168deg,rgba(192,167,134,.22) 0%,rgba(192,167,134,.05) 18%,rgba(192,167,134,0) 34%)',
  },
  special_care: {
    background: 'linear-gradient(152deg,#1D3560 0%,#132543 50%,#0A1425 100%)',
    border: '1px solid rgba(192,167,134,.85)',
    sheen: 'radial-gradient(120% 90% at 78% 8%,rgba(192,167,134,.30) 0%,rgba(192,167,134,.08) 42%,rgba(192,167,134,0) 70%)',
  },
};

/** The brand mark. Two colours, made of light — flame in Candlelight, body in
 *  Candle White, exactly as the brand book requires. */
function CandleMark({ size = 30 }) {
  return (
    <Box component="svg" viewBox="0 0 132 232" aria-hidden="true"
      sx={{ width: size, height: 'auto', display: 'block',
        filter: 'drop-shadow(0 0 14px rgba(192,167,134,.45))' }}>
      <path fill="#C0A786" d="M43 1C40.5 0.5 31 38 63 61C73 63.5 73 18 43 1Z" />
      <path fill="#FFFFFF"
        d="M0 71H58L66 75L74 71H132V75C105 119 105 188 132 232H0V232C27 188 27 119 0 75Z" />
    </Box>
  );
}

/**
 * @param {{tier: string, holderName?: string, keptUntil?: string|null,
 *          tierLabel: string, validLabel: string, noCoverLabel: string,
 *          formatDate: (iso: string) => string}} props
 */
export default function CandleCardHero({
  tier, holderName, keptUntil, tierLabel, validLabel, noCoverLabel, formatDate,
}) {
  const material = MATERIAL[tier] || MATERIAL.free;

  return (
    <Box
      sx={{
        position: 'relative',
        // ISO 7810 ID-1 — the proportion of every card in a wallet. Holding
        // the real ratio is most of what makes this read as an object.
        aspectRatio: '1.586 / 1',
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
        borderRadius: 4,
        overflow: 'hidden',
        background: material.background,
        border: material.border,
        boxShadow: '0 18px 40px -22px rgba(10,20,37,.85)',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { xs: 2.25, sm: 2.75 },
      }}
    >
      {material.sheen && (
        <Box aria-hidden="true" sx={{
          position: 'absolute', inset: 0, background: material.sheen, pointerEvents: 'none',
        }} />
      )}

      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', gap: 2 }}>
        <CandleMark />
        <Typography sx={{
          fontWeight: 700, fontSize: 13, letterSpacing: '.02em',
          color: '#C0A786', textAlign: 'end', lineHeight: 1.3, pt: .25,
        }}>
          {tierLabel}
        </Typography>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {holderName ? (
          <Typography sx={{ fontWeight: 600, fontSize: { xs: 17, sm: 19 }, lineHeight: 1.25 }}>
            {holderName}
          </Typography>
        ) : null}
        <Typography sx={{ mt: .75, fontSize: 11.5, color: 'rgba(255,255,255,.62)' }}>
          {validLabel}
        </Typography>
        <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 600,
          color: keptUntil ? '#FFFFFF' : 'rgba(255,255,255,.55)' }}>
          {keptUntil ? formatDate(keptUntil) : noCoverLabel}
        </Typography>
      </Box>
    </Box>
  );
}
